import {
  Alert,
  Box,
  Card,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AutoStoriesRounded, SearchRounded } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  getChapter,
  listAvailableBooks,
  parseReference,
  type ScriptureRef,
} from '../scripture/rv1960-stub';

export function ScripturePage(): JSX.Element {
  const { t } = useTranslation('sis');
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('ref') ?? 'Juan 3:16';
  const [search, setSearch] = useState(initial);
  const [ref, setRef] = useState<ScriptureRef | null>(() => parseReference(initial));
  const [error, setError] = useState<string | null>(null);

  // Sync URL → state when navigating in via a ScriptureRefText link
  useEffect(() => {
    const fromUrl = searchParams.get('ref');
    if (fromUrl && fromUrl !== search) {
      setSearch(fromUrl);
      const parsed = parseReference(fromUrl);
      setRef(parsed);
      setError(parsed ? null : t('portal.scripture.notLoadedShort'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (q: string) => {
    setSearch(q);
    const parsed = parseReference(q);
    if (parsed) {
      setRef(parsed);
      setError(null);
      setSearchParams({ ref: q }, { replace: true });
    } else if (q.trim()) {
      setError(t('portal.scripture.unrecognizedFormat'));
    }
  };

  const chapter = ref ? getChapter(ref.book, ref.chapter) : null;

  const verseFilter = useMemo(() => {
    if (!ref?.verses || !chapter) return chapter?.verses ?? [];
    const ranges = ref.verses.split(',').map((p) => p.trim());
    const wanted = new Set<number>();
    for (const r of ranges) {
      const m = r.match(/^(\d+)(?:-(\d+))?$/);
      if (!m) continue;
      const start = parseInt(m[1]!, 10);
      const end = m[2] ? parseInt(m[2], 10) : start;
      for (let v = start; v <= end; v++) wanted.add(v);
    }
    return chapter.verses.filter((v) => wanted.has(v.verse));
  }, [ref, chapter]);

  const availableBooks = listAvailableBooks();

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#e6edf3">
          {t('portal.scripture.title')}
        </Typography>
        <Typography variant="body2" color="#8b949e">
          {t('portal.scripture.subtitle')}
        </Typography>
      </Box>

      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Stack spacing={2}>
          <TextField
            size="small"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('portal.scripture.searchPlaceholder')}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: '#6e7681' }} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {error && <Alert severity="warning">{error}</Alert>}
          <Box>
            <Typography variant="caption" color="#8b949e" mb={1} display="block">
              {t('portal.scripture.availableBooks')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {availableBooks.map((b) => (
                <Chip
                  key={b}
                  size="small"
                  label={b}
                  onClick={() => handleSearch(`${b} 1`)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: 'rgba(99,102,241,0.1)',
                    color: '#a5b4fc',
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Card>

      {chapter ? (
        <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 3 }}>
          <Stack direction="row" alignItems="center" mb={2}>
            <AutoStoriesRounded sx={{ color: '#6366f1', mr: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#e6edf3">
              {chapter.book} {chapter.chapter}
              {ref?.verses && `:${ref.verses}`}
            </Typography>
            <Chip
              size="small"
              label="RV1960"
              sx={{ ml: 2, bgcolor: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}
            />
          </Stack>
          <Stack spacing={1.5}>
            {verseFilter.map((v) => (
              <Box key={v.verse}>
                <Typography
                  component="span"
                  variant="caption"
                  color="#6366f1"
                  sx={{ fontWeight: 700, mr: 1 }}
                >
                  {v.verse}
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  color="#c9d1d9"
                  sx={{ lineHeight: 1.7 }}
                >
                  {v.text}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>
      ) : (
        <Alert severity="info">{t('portal.scripture.notLoadedFull')}</Alert>
      )}
    </Stack>
  );
}
